import type { Column, Table } from '@tanstack/react-table';
import { useMemo } from 'react';

type StringItem = {
    id: string;
    label: string;
    value: string;
};

type UseStringFilterDataProps<TData, TValue> = {
    disabled: boolean;
    table: Table<TData>;
    column: Column<TData, TValue>;
};

export function useStringFilterData<TData, TValue>({
    disabled,
    table,
    column,
}: UseStringFilterDataProps<TData, TValue>) {
    return useMemo(() => {
        if (disabled) {
            return {
                items: [],
                allIds: [],
            };
        }

        // Get all values and create a map of lowercase -> original case
        const valueMap = new Map<string, string>();

        table.options.data.forEach((row) => {
            const value = (row as Record<string, string | null | undefined>)[column.id];

            if (value === null || value === undefined) {
                return;
            }

            const lowerValue = value.toLowerCase();

            // only store the first occurrence of each case-insensitive value
            if (!valueMap.has(lowerValue)) {
                valueMap.set(lowerValue, value);
            }
        });

        // sort the unique values
        const uniqueValues = Array.from(valueMap.entries()).sort(([a], [b]) => a.localeCompare(b));

        // create items array with a select all option at the top
        const items: StringItem[] = [
            {
                id: 'select-all',
                label: '(Alle auswählen)',
                value: '',
            },
            ...uniqueValues.map(([lowerValue, originalValue]) => ({
                id: `value-${lowerValue}`,
                label: originalValue,
                value: originalValue,
            })),
            {
                id: 'select-blanks',
                label: '(Leere auswählen)',
                value: '',
            },
        ];

        return {
            items,
            allIds: items.map((item) => item.id),
        };
    }, [column, disabled, table.options.data]);
}
